import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { 
  Save, Globe, Mail, Phone, MapPin, Linkedin, Twitter, 
  Facebook, Instagram, Youtube, Home, FileText, Search,
  Plus, X, Eye, Settings, Image, Type, LayoutDashboard
} from 'lucide-react';
import { settingsService, mediaService, heroCardService } from '../../services/api';
import LoadingSpinner from '../../components/ui/LoadingSpinner';

function AdminSettingsPage() {
  const [settings, setSettings] = useState({
    // Général
    site_name: '',
    site_tagline: '',
    site_description: '',
    about_text: '',
    logo_url: '',
    favicon_url: '',
    
    // Contact
    contact_email: '',
    contact_phone: '',
    contact_address: '',
    contact_map_url: '',
    
    // Réseaux sociaux
    social_linkedin: '',
    social_twitter: '',
    social_facebook: '',
    social_instagram: '',
    social_youtube: '',
    
    // Page d'accueil
    hero_title: '',
    hero_subtitle: '',
    hero_badge: '',
    hero_cta_text: '',
    hero_cta_link: '',
    hero_secondary_text: '',
    hero_secondary_link: '',
    trust_clients: [],
    
    // Footer
    footer_text: '',
    footer_description: '',
    
    // SEO
    meta_title: '',
    meta_description: '',
    meta_keywords: ''
  });

  // État pour la carte Hero
  const [heroCard, setHeroCard] = useState({
    title: 'Dashboard Analytics',
    subtitle: 'Performance en temps réel',
    badge_text: 'Live',
    certification_title: 'ISO 20022',
    certification_status: 'Certifié',
    stat1_value: '98%',
    stat1_label: 'Uptime',
    stat2_value: '2.5s',
    stat2_label: 'Latence',
    stat3_value: '15K',
    stat3_label: 'Transactions',
    floating_stat_value: '+50 Projets',
    floating_stat_label: 'En Afrique'
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [newClient, setNewClient] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadingField, setUploadingField] = useState('');

  const tabs = [
    { id: 'general', label: 'Général', icon: Settings },
    { id: 'homepage', label: 'Page d\'accueil', icon: Home },
    { id: 'herocard', label: 'Carte Hero', icon: LayoutDashboard },
    { id: 'contact', label: 'Contact', icon: Mail },
    { id: 'social', label: 'Réseaux sociaux', icon: Globe },
    { id: 'footer', label: 'Footer', icon: FileText },
    { id: 'seo', label: 'SEO', icon: Search },
  ];

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const [settingsRes, heroCardRes] = await Promise.all([
        settingsService.getAll(),
        heroCardService.get().catch(() => ({ data: { heroCard: {} } }))
      ]);

      if (settingsRes.data.settings) {
        setSettings(prev => ({
          ...prev,
          ...settingsRes.data.settings,
          trust_clients: Array.isArray(settingsRes.data.settings.trust_clients) 
            ? settingsRes.data.settings.trust_clients 
            : []
        }));
      }

      if (heroCardRes.data.heroCard) {
        setHeroCard(prev => ({
          ...prev,
          ...heroCardRes.data.heroCard
        }));
      }
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur lors du chargement des paramètres');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await Promise.all([
        settingsService.update(settings),
        heroCardService.update(heroCard)
      ]);
      toast.success('Paramètres enregistrés avec succès !');
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const updateHeroCard = (key, value) => {
    setHeroCard(prev => ({ ...prev, [key]: value }));
  };

  const handleImageUpload = async (e, key) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/') && !file.name.endsWith('.ico')) {
      toast.error('Veuillez sélectionner une image');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image trop lourde (max 5MB)');
      return;
    }

    setUploading(true);
    setUploadingField(key);
    try {
      const response = await mediaService.uploadImage(file);
      const BASE_URL = import.meta.env.VITE_API_URL?.replace('/api', '') || 'http://localhost:5000';
      const imageUrl = BASE_URL + response.data.media.url;
      updateSetting(key, imageUrl);
      toast.success('Image uploadée avec succès !');
    } catch (error) {
      console.error('Erreur upload:', error);
      toast.error('Erreur lors de l\'upload');
    } finally {
      setUploading(false);
      setUploadingField('');
    }
  };

  const addClient = () => {
    if (newClient.trim()) {
      updateSetting('trust_clients', [...(settings.trust_clients || []), newClient.trim()]);
      setNewClient('');
    }
  };

  const removeClient = (index) => {
    updateSetting('trust_clients', settings.trust_clients.filter((_, i) => i !== index));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="pb-24">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-dark-900">Paramètres du site</h1>
          <p className="text-dark-500">Configurez tous les éléments de votre site</p>
        </div>
        <button 
          onClick={handleSave} 
          disabled={saving} 
          className="btn-primary"
        >
          {saving ? (
            <>
              <LoadingSpinner size="sm" />
              <span className="ml-2">Enregistrement...</span>
            </>
          ) : (
            <>
              <Save className="w-5 h-5 mr-2" />
              Enregistrer tout
            </>
          )}
        </button>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-sm border border-dark-100 mb-6">
        <div className="flex overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-6 py-4 font-medium transition-colors whitespace-nowrap border-b-2 ${
                activeTab === tab.id
                  ? 'text-primary-600 border-primary-600 bg-primary-50/50'
                  : 'text-dark-500 border-transparent hover:text-dark-700 hover:bg-dark-50'
              }`}
            >
              <tab.icon className="w-5 h-5" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Contenu des tabs */}
      <motion.div
        key={activeTab}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
      >
        {/* Tab Général */}
        {activeTab === 'general' && (
          <div className="grid lg:grid-cols-2 gap-6">
            <div className="card p-6">
              <h2 className="text-lg font-semibold text-dark-900 mb-6 flex items-center gap-2">
                <Type className="w-5 h-5 text-primary-600" />
                Identité du site
              </h2>
              <div className="space-y-4">
                <div>
                  <label className="label">Nom du site</label>
                  <input
                    type="text"
                    value={settings.site_name || ''}
                    onChange={(e) => updateSetting('site_name', e.target.value)}
                    className="input"
                    placeholder="WealthTech Pro"
                  />
                </div>
                <div>
                  <label className="label">Slogan / Tagline</label>
                  <input
                    type="text"
                    value={settings.site_tagline || ''}
                    onChange={(e) => updateSetting('site_tagline', e.target.value)}
                    className="input"
                    placeholder="Solutions WealthTech & ISO 20022"
                  />
                </div>
                <div>
                  <label className="label">Description courte</label>
                  <textarea
                    value={settings.site_description || ''}
                    onChange={(e) => updateSetting('site_description', e.target.value)}
                    className="input"
                    rows={3}
                    placeholder="Votre partenaire de confiance..."
                  />
                </div>
                <div>
                  <label className="label">Texte À propos</label>
                  <textarea
                    value={settings.about_text || ''}
                    onChange={(e) => updateSetting('about_text', e.target.value)}
                    className="input"
                    rows={4}
                    placeholder="Plus de 15 ans d'expertise..."
                  />
                </div>
              </div>
            </div>

            <div className="card p-6">
              <h2 className="text-lg font-semibold text-dark-900 mb-6 flex items-center gap-2">
                <Image className="w-5 h-5 text-primary-600" />
                Images
              </h2>
              <div className="space-y-6">
                {/* Logo du site */}
                <div>
                  <label className="label">Logo du site</label>
                  <div className="flex items-start gap-4">
                    <div className="relative">
                      {settings.logo_url ? (
                        <div className="relative">
                          <img 
                            src={settings.logo_url} 
                            alt="Logo" 
                            className="w-32 h-32 object-contain border border-dark-200 rounded-xl bg-white p-2"
                          />
                          <button
                            type="button"
                            onClick={() => updateSetting('logo_url', '')}
                            className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <div className="w-32 h-32 border-2 border-dashed border-dark-300 rounded-xl flex items-center justify-center bg-dark-50">
                          <Image className="w-10 h-10 text-dark-300" />
                        </div>
                      )}
                    </div>
                    
                    <div className="flex-1">
                      <label className="block w-full cursor-pointer">
                        <div className="border-2 border-dashed border-dark-200 rounded-xl p-6 text-center hover:border-primary-400 hover:bg-primary-50/50 transition-colors">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleImageUpload(e, 'logo_url')}
                            className="hidden"
                            disabled={uploading}
                          />
                          {uploading && uploadingField === 'logo_url' ? (
                            <div className="flex flex-col items-center">
                              <LoadingSpinner size="sm" />
                              <p className="text-sm text-dark-500 mt-2">Upload en cours...</p>
                            </div>
                          ) : (
                            <div>
                              <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-3">
                                <Plus className="w-6 h-6 text-primary-600" />
                              </div>
                              <p className="text-dark-700 font-medium">Cliquez pour choisir un logo</p>
                              <p className="text-dark-400 text-sm mt-1">PNG, JPG, SVG (max 5MB)</p>
                            </div>
                          )}
                        </div>
                      </label>
                      <p className="text-xs text-dark-400 mt-2">
                        💡 Recommandé : Image carrée ou horizontale, fond transparent (PNG)
                      </p>
                    </div>
                  </div>
                </div>

                {/* Favicon */}
                <div>
                  <label className="label">Favicon</label>
                  <div className="flex items-start gap-4">
                    <div className="relative">
                      {settings.favicon_url ? (
                        <div className="relative">
                          <img 
                            src={settings.favicon_url} 
                            alt="Favicon" 
                            className="w-16 h-16 object-contain border border-dark-200 rounded-lg bg-white p-1"
                          />
                          <button
                            type="button"
                            onClick={() => updateSetting('favicon_url', '')}
                            className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ) : (
                        <div className="w-16 h-16 border-2 border-dashed border-dark-300 rounded-lg flex items-center justify-center bg-dark-50">
                          <Globe className="w-6 h-6 text-dark-300" />
                        </div>
                      )}
                    </div>
                    
                    <div className="flex-1">
                      <label className="block w-full cursor-pointer">
                        <div className="border-2 border-dashed border-dark-200 rounded-xl p-4 text-center hover:border-primary-400 hover:bg-primary-50/50 transition-colors">
                          <input
                            type="file"
                            accept="image/*,.ico"
                            onChange={(e) => handleImageUpload(e, 'favicon_url')}
                            className="hidden"
                            disabled={uploading}
                          />
                          {uploading && uploadingField === 'favicon_url' ? (
                            <LoadingSpinner size="sm" />
                          ) : (
                            <div className="flex items-center justify-center gap-3">
                              <Plus className="w-5 h-5 text-primary-600" />
                              <span className="text-dark-600">Choisir un favicon</span>
                            </div>
                          )}
                        </div>
                      </label>
                      <p className="text-xs text-dark-400 mt-2">
                        💡 Recommandé : Image carrée 32x32 ou 64x64 pixels (ICO, PNG)
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tab Page d'accueil */}
        {activeTab === 'homepage' && (
          <div className="space-y-6">
            <div className="card p-6">
              <h2 className="text-lg font-semibold text-dark-900 mb-6 flex items-center gap-2">
                <Home className="w-5 h-5 text-primary-600" />
                Section Hero
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label className="label">Badge / Étiquette</label>
                  <input
                    type="text"
                    value={settings.hero_badge || ''}
                    onChange={(e) => updateSetting('hero_badge', e.target.value)}
                    className="input"
                    placeholder="Leader en transformation digitale financière"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="label">Titre principal (Hero)</label>
                  <input
                    type="text"
                    value={settings.hero_title || ''}
                    onChange={(e) => updateSetting('hero_title', e.target.value)}
                    className="input"
                    placeholder="Solutions WealthTech & ISO 20022"
                  />
                  <p className="text-xs text-dark-400 mt-1">
                    Astuce: Les mots "WealthTech" et "ISO 20022" seront automatiquement colorés en dégradé
                  </p>
                </div>
                <div className="md:col-span-2">
                  <label className="label">Sous-titre / Description</label>
                  <textarea
                    value={settings.hero_subtitle || ''}
                    onChange={(e) => updateSetting('hero_subtitle', e.target.value)}
                    className="input"
                    rows={3}
                    placeholder="Votre partenaire de confiance pour la transformation digitale..."
                  />
                </div>
                <div>
                  <label className="label">Texte bouton principal</label>
                  <input
                    type="text"
                    value={settings.hero_cta_text || ''}
                    onChange={(e) => updateSetting('hero_cta_text', e.target.value)}
                    className="input"
                    placeholder="Demander un audit gratuit"
                  />
                </div>
                <div>
                  <label className="label">Lien bouton principal</label>
                  <input
                    type="text"
                    value={settings.hero_cta_link || ''}
                    onChange={(e) => updateSetting('hero_cta_link', e.target.value)}
                    className="input"
                    placeholder="/contact"
                  />
                </div>
                <div>
                  <label className="label">Texte bouton secondaire</label>
                  <input
                    type="text"
                    value={settings.hero_secondary_text || ''}
                    onChange={(e) => updateSetting('hero_secondary_text', e.target.value)}
                    className="input"
                    placeholder="Découvrir nos services"
                  />
                </div>
                <div>
                  <label className="label">Lien bouton secondaire</label>
                  <input
                    type="text"
                    value={settings.hero_secondary_link || ''}
                    onChange={(e) => updateSetting('hero_secondary_link', e.target.value)}
                    className="input"
                    placeholder="/services"
                  />
                </div>
              </div>
            </div>

            <div className="card p-6">
              <h2 className="text-lg font-semibold text-dark-900 mb-6">
                Clients de confiance
              </h2>
              <p className="text-dark-500 text-sm mb-4">
                Ces noms s'afficheront dans la section "Ils nous font confiance"
              </p>
              <div className="flex flex-wrap gap-2 mb-4">
                {(settings.trust_clients || []).map((client, index) => (
                  <span 
                    key={index}
                    className="inline-flex items-center gap-2 px-3 py-2 bg-dark-100 rounded-lg"
                  >
                    {client}
                    <button 
                      onClick={() => removeClient(index)}
                      className="text-dark-400 hover:text-red-500"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </span>
                ))}
                {(settings.trust_clients || []).length === 0 && (
                  <span className="text-dark-400 text-sm">Aucun client ajouté</span>
                )}
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newClient}
                  onChange={(e) => setNewClient(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addClient())}
                  className="input flex-1"
                  placeholder="Nom du client (ex: Ecobank)"
                />
                <button onClick={addClient} className="btn-primary">
                  <Plus className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Aperçu */}
            <div className="card p-6 bg-gradient-to-br from-dark-50 to-primary-50">
              <div className="flex items-center gap-2 mb-4">
                <Eye className="w-5 h-5 text-primary-600" />
                <h3 className="font-semibold text-dark-900">Aperçu Hero</h3>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-sm">
                {settings.hero_badge && (
                  <span className="inline-block px-3 py-1 bg-primary-100 text-primary-700 text-sm rounded-full mb-3">
                    ⚡ {settings.hero_badge}
                  </span>
                )}
                <h1 className="text-2xl font-bold text-dark-900 mb-2">
                  {settings.hero_title || 'Titre principal'}
                </h1>
                <p className="text-dark-600 mb-4">
                  {settings.hero_subtitle || 'Sous-titre...'}
                </p>
                <div className="flex gap-2 flex-wrap">
                  <span className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm">
                    {settings.hero_cta_text || 'Bouton principal'}
                  </span>
                  <span className="px-4 py-2 border border-dark-200 text-dark-700 rounded-lg text-sm">
                    {settings.hero_secondary_text || 'Bouton secondaire'}
                  </span>
                </div>
                {settings.trust_clients?.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-dark-100">
                    <p className="text-xs text-dark-400 mb-2">Ils nous font confiance</p>
                    <div className="flex gap-4 text-sm text-dark-600 flex-wrap">
                      {settings.trust_clients.map((client, i) => (
                        <span key={i}>{client}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Tab Carte Hero */}
        {activeTab === 'herocard' && (
          <div className="space-y-6">
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Formulaire */}
              <div className="space-y-6">
                <div className="card p-6">
                  <h2 className="text-lg font-semibold text-dark-900 mb-6 flex items-center gap-2">
                    <LayoutDashboard className="w-5 h-5 text-primary-600" />
                    Carte Dashboard
                  </h2>
                  <div className="space-y-4">
                    <div>
                      <label className="label">Titre</label>
                      <input
                        type="text"
                        value={heroCard.title || ''}
                        onChange={(e) => updateHeroCard('title', e.target.value)}
                        className="input"
                        placeholder="Dashboard Analytics"
                      />
                    </div>
                    <div>
                      <label className="label">Sous-titre</label>
                      <input
                        type="text"
                        value={heroCard.subtitle || ''}
                        onChange={(e) => updateHeroCard('subtitle', e.target.value)}
                        className="input"
                        placeholder="Performance en temps réel"
                      />
                    </div>
                    <div>
                      <label className="label">Badge (ex: Live)</label>
                      <input
                        type="text"
                        value={heroCard.badge_text || ''}
                        onChange={(e) => updateHeroCard('badge_text', e.target.value)}
                        className="input"
                        placeholder="Live"
                      />
                    </div>
                  </div>
                </div>

                <div className="card p-6">
                  <h2 className="text-lg font-semibold text-dark-900 mb-6">Certification</h2>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="label">Titre certification</label>
                      <input
                        type="text"
                        value={heroCard.certification_title || ''}
                        onChange={(e) => updateHeroCard('certification_title', e.target.value)}
                        className="input"
                        placeholder="ISO 20022"
                      />
                    </div>
                    <div>
                      <label className="label">Statut</label>
                      <input
                        type="text"
                        value={heroCard.certification_status || ''}
                        onChange={(e) => updateHeroCard('certification_status', e.target.value)}
                        className="input"
                        placeholder="Certifié"
                      />
                    </div>
                  </div>
                </div>

                <div className="card p-6">
                  <h2 className="text-lg font-semibold text-dark-900 mb-6">Statistiques</h2>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="label">Stat 1 - Valeur</label>
                        <input
                          type="text"
                          value={heroCard.stat1_value || ''}
                          onChange={(e) => updateHeroCard('stat1_value', e.target.value)}
                          className="input"
                          placeholder="98%"
                        />
                      </div>
                      <div>
                        <label className="label">Stat 1 - Label</label>
                        <input
                          type="text"
                          value={heroCard.stat1_label || ''}
                          onChange={(e) => updateHeroCard('stat1_label', e.target.value)}
                          className="input"
                          placeholder="Uptime"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="label">Stat 2 - Valeur</label>
                        <input
                          type="text"
                          value={heroCard.stat2_value || ''}
                          onChange={(e) => updateHeroCard('stat2_value', e.target.value)}
                          className="input"
                          placeholder="2.5s"
                        />
                      </div>
                      <div>
                        <label className="label">Stat 2 - Label</label>
                        <input
                          type="text"
                          value={heroCard.stat2_label || ''}
                          onChange={(e) => updateHeroCard('stat2_label', e.target.value)}
                          className="input"
                          placeholder="Latence"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="label">Stat 3 - Valeur</label>
                        <input
                          type="text"
                          value={heroCard.stat3_value || ''}
                          onChange={(e) => updateHeroCard('stat3_value', e.target.value)}
                          className="input"
                          placeholder="15K"
                        />
                      </div>
                      <div>
                        <label className="label">Stat 3 - Label</label>
                        <input
                          type="text"
                          value={heroCard.stat3_label || ''}
                          onChange={(e) => updateHeroCard('stat3_label', e.target.value)}
                          className="input"
                          placeholder="Transactions"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="card p-6">
                  <h2 className="text-lg font-semibold text-dark-900 mb-6">Bulle flottante</h2>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="label">Valeur</label>
                      <input
                        type="text"
                        value={heroCard.floating_stat_value || ''}
                        onChange={(e) => updateHeroCard('floating_stat_value', e.target.value)}
                        className="input"
                        placeholder="+50 Projets"
                      />
                    </div>
                    <div>
                      <label className="label">Label</label>
                      <input
                        type="text"
                        value={heroCard.floating_stat_label || ''}
                        onChange={(e) => updateHeroCard('floating_stat_label', e.target.value)}
                        className="input"
                        placeholder="En Afrique"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Aperçu en temps réel */}
              <div className="lg:sticky lg:top-24">
                <div className="card p-6 bg-gradient-to-br from-dark-50 to-primary-50">
                  <div className="flex items-center gap-2 mb-4">
                    <Eye className="w-5 h-5 text-primary-600" />
                    <h3 className="font-semibold text-dark-900">Aperçu en temps réel</h3>
                  </div>
                  
                  <div className="relative">
                    <div className="bg-white rounded-2xl shadow-xl p-6 border border-dark-100">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center">
                            <LayoutDashboard className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <p className="font-semibold text-dark-900">{heroCard.title || 'Dashboard Analytics'}</p>
                            <p className="text-sm text-dark-500">{heroCard.subtitle || 'Performance en temps réel'}</p>
                          </div>
                        </div>
                        <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                          {heroCard.badge_text || 'Live'}
                        </span>
                      </div>
                      
                      <div className="h-32 bg-gradient-to-br from-primary-50 to-secondary-50 rounded-xl flex items-end justify-around p-3 mb-4">
                        {[40, 65, 45, 80, 55, 90, 70].map((h, i) => (
                          <div
                            key={i}
                            style={{ height: `${h}%` }}
                            className="w-6 bg-gradient-to-t from-primary-500 to-primary-400 rounded-t-lg"
                          />
                        ))}
                      </div>

                      <div className="grid grid-cols-3 gap-3">
                        <div className="text-center p-2 bg-dark-50 rounded-lg">
                          <p className="text-lg font-bold text-primary-600">{heroCard.stat1_value || '98%'}</p>
                          <p className="text-xs text-dark-500">{heroCard.stat1_label || 'Uptime'}</p>
                        </div>
                        <div className="text-center p-2 bg-dark-50 rounded-lg">
                          <p className="text-lg font-bold text-secondary-600">{heroCard.stat2_value || '2.5s'}</p>
                          <p className="text-xs text-dark-500">{heroCard.stat2_label || 'Latence'}</p>
                        </div>
                        <div className="text-center p-2 bg-dark-50 rounded-lg">
                          <p className="text-lg font-bold text-accent-600">{heroCard.stat3_value || '15K'}</p>
                          <p className="text-xs text-dark-500">{heroCard.stat3_label || 'Transactions'}</p>
                        </div>
                      </div>
                    </div>

                    {/* Certification flottante */}
                    <div className="absolute -top-4 -right-4 bg-white rounded-xl shadow-lg p-3 border border-dark-100">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                          <svg className="w-4 h-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        <div>
                          <p className="font-medium text-dark-900 text-sm">{heroCard.certification_title || 'ISO 20022'}</p>
                          <p className="text-xs text-green-600">{heroCard.certification_status || 'Certifié'}</p>
                        </div>
                      </div>
                    </div>

                    {/* Bulle flottante */}
                    <div className="absolute -bottom-2 -left-4 bg-white rounded-xl shadow-lg p-3 border border-dark-100">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                          <Globe className="w-4 h-4 text-primary-600" />
                        </div>
                        <div>
                          <p className="font-medium text-dark-900 text-sm">{heroCard.floating_stat_value || '+50 Projets'}</p>
                          <p className="text-xs text-dark-500">{heroCard.floating_stat_label || 'En Afrique'}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tab Contact */}
        {activeTab === 'contact' && (
          <div className="card p-6">
            <h2 className="text-lg font-semibold text-dark-900 mb-6 flex items-center gap-2">
              <Mail className="w-5 h-5 text-primary-600" />
              Informations de contact
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="label flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Email
                </label>
                <input
                  type="email"
                  value={settings.contact_email || ''}
                  onChange={(e) => updateSetting('contact_email', e.target.value)}
                  className="input"
                  placeholder="contact@wealthtech-pro.com"
                />
              </div>
              <div>
                <label className="label flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  Téléphone
                </label>
                <input
                  type="tel"
                  value={settings.contact_phone || ''}
                  onChange={(e) => updateSetting('contact_phone', e.target.value)}
                  className="input"
                  placeholder="+225 07 67 81 19 99"
                />
              </div>
              <div className="md:col-span-2">
                <label className="label flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Adresse
                </label>
                <input
                  type="text"
                  value={settings.contact_address || ''}
                  onChange={(e) => updateSetting('contact_address', e.target.value)}
                  className="input"
                  placeholder="Abidjan, Côte d'Ivoire"
                />
              </div>
              <div className="md:col-span-2">
                <label className="label">URL Google Maps (embed)</label>
                <input
                  type="url"
                  value={settings.contact_map_url || ''}
                  onChange={(e) => updateSetting('contact_map_url', e.target.value)}
                  className="input"
                  placeholder="https://www.google.com/maps/embed?..."
                />
              </div>
            </div>
          </div>
        )}

        {/* Tab Réseaux sociaux */}
        {activeTab === 'social' && (
          <div className="card p-6">
            <h2 className="text-lg font-semibold text-dark-900 mb-6 flex items-center gap-2">
              <Globe className="w-5 h-5 text-primary-600" />
              Réseaux sociaux
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="label flex items-center gap-2">
                  <Linkedin className="w-4 h-4 text-[#0077b5]" />
                  LinkedIn
                </label>
                <input
                  type="url"
                  value={settings.social_linkedin || ''}
                  onChange={(e) => updateSetting('social_linkedin', e.target.value)}
                  className="input"
                  placeholder="https://linkedin.com/company/..."
                />
              </div>
              <div>
                <label className="label flex items-center gap-2">
                  <Twitter className="w-4 h-4 text-[#1da1f2]" />
                  Twitter / X
                </label>
                <input
                  type="url"
                  value={settings.social_twitter || ''}
                  onChange={(e) => updateSetting('social_twitter', e.target.value)}
                  className="input"
                  placeholder="https://twitter.com/..."
                />
              </div>
              <div>
                <label className="label flex items-center gap-2">
                  <Facebook className="w-4 h-4 text-[#1877f2]" />
                  Facebook
                </label>
                <input
                  type="url"
                  value={settings.social_facebook || ''}
                  onChange={(e) => updateSetting('social_facebook', e.target.value)}
                  className="input"
                  placeholder="https://facebook.com/..."
                />
              </div>
              <div>
                <label className="label flex items-center gap-2">
                  <Instagram className="w-4 h-4 text-[#e4405f]" />
                  Instagram
                </label>
                <input
                  type="url"
                  value={settings.social_instagram || ''}
                  onChange={(e) => updateSetting('social_instagram', e.target.value)}
                  className="input"
                  placeholder="https://instagram.com/..."
                />
              </div>
              <div className="md:col-span-2">
                <label className="label flex items-center gap-2">
                  <Youtube className="w-4 h-4 text-[#ff0000]" />
                  YouTube
                </label>
                <input
                  type="url"
                  value={settings.social_youtube || ''}
                  onChange={(e) => updateSetting('social_youtube', e.target.value)}
                  className="input"
                  placeholder="https://youtube.com/..."
                />
              </div>
            </div>
          </div>
        )}

        {/* Tab Footer */}
        {activeTab === 'footer' && (
          <div className="card p-6">
            <h2 className="text-lg font-semibold text-dark-900 mb-6 flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary-600" />
              Pied de page (Footer)
            </h2>
            <div className="space-y-4">
              <div>
                <label className="label">Description du footer</label>
                <textarea
                  value={settings.footer_description || ''}
                  onChange={(e) => updateSetting('footer_description', e.target.value)}
                  className="input"
                  rows={3}
                  placeholder="Expert en solutions WealthTech et conformité ISO 20022..."
                />
              </div>
              <div>
                <label className="label">Texte de copyright</label>
                <input
                  type="text"
                  value={settings.footer_text || ''}
                  onChange={(e) => updateSetting('footer_text', e.target.value)}
                  className="input"
                  placeholder="© 2024 WealthTech Pro. Tous droits réservés."
                />
              </div>
            </div>
          </div>
        )}

        {/* Tab SEO */}
        {activeTab === 'seo' && (
          <div className="card p-6">
            <h2 className="text-lg font-semibold text-dark-900 mb-6 flex items-center gap-2">
              <Search className="w-5 h-5 text-primary-600" />
              Optimisation SEO
            </h2>
            <div className="space-y-4">
              <div>
                <label className="label">Meta Title</label>
                <input
                  type="text"
                  value={settings.meta_title || ''}
                  onChange={(e) => updateSetting('meta_title', e.target.value)}
                  className="input"
                  placeholder="WealthTech Pro - Solutions WealthTech & ISO 20022"
                />
                <p className="text-xs text-dark-400 mt-1">
                  Recommandé: 50-60 caractères ({(settings.meta_title || '').length} caractères)
                </p>
              </div>
              <div>
                <label className="label">Meta Description</label>
                <textarea
                  value={settings.meta_description || ''}
                  onChange={(e) => updateSetting('meta_description', e.target.value)}
                  className="input"
                  rows={3}
                  placeholder="Expert en solutions WealthTech et conformité ISO 20022..."
                />
                <p className="text-xs text-dark-400 mt-1">
                  Recommandé: 150-160 caractères ({(settings.meta_description || '').length} caractères)
                </p>
              </div>
              <div>
                <label className="label">Meta Keywords</label>
                <input
                  type="text"
                  value={settings.meta_keywords || ''}
                  onChange={(e) => updateSetting('meta_keywords', e.target.value)}
                  className="input"
                  placeholder="wealthtech, iso 20022, banque, finance, afrique"
                />
                <p className="text-xs text-dark-400 mt-1">
                  Séparez les mots-clés par des virgules
                </p>
              </div>

              {/* Aperçu Google */}
              <div className="mt-6 p-4 bg-dark-50 rounded-xl">
                <p className="text-xs text-dark-400 mb-2">Aperçu dans Google</p>
                <div className="bg-white p-4 rounded-lg">
                  <p className="text-blue-700 text-lg hover:underline cursor-pointer">
                    {settings.meta_title || 'Titre de la page'}
                  </p>
                  <p className="text-green-700 text-sm">
                    https://wealthtech-pro.com
                  </p>
                  <p className="text-dark-600 text-sm mt-1">
                    {settings.meta_description || 'Description de la page...'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </motion.div>

      {/* Bouton flottant de sauvegarde */}
      <motion.button
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        onClick={handleSave}
        disabled={saving}
        className="fixed bottom-8 right-8 btn-primary shadow-lg shadow-primary-500/30"
      >
        {saving ? (
          <>
            <LoadingSpinner size="sm" />
            <span className="ml-2">Enregistrement...</span>
          </>
        ) : (
          <>
            <Save className="w-5 h-5 mr-2" />
            Enregistrer
          </>
        )}
      </motion.button>
    </div>
  );
}

export default AdminSettingsPage;